// Auto-marking helpers for the quiz engine.
export type QType = "mcq" | "true_false" | "fill_blank" | "matching" | "essay" | "short";

export interface Question {
  id: string;
  type: QType;
  question: string;
  options: any;
  correct_answer: string | null;
  explanation: string | null;
  marks: number;
  difficulty?: "easy" | "medium" | "hard";
}

export type Answer = string | string[] | Record<string, string> | null;

export interface GradeResult {
  score: number;
  isCorrect: boolean | null; // null = needs manual grading
  needsGrading: boolean;
}

function norm(s: string) { return s.trim().toLowerCase().replace(/\s+/g, " "); }

export function gradeQuestion(q: Question, answer: Answer): GradeResult {
  if (answer === null || answer === undefined || answer === "") {
    if (q.type === "essay") return { score: 0, isCorrect: null, needsGrading: true };
    return { score: 0, isCorrect: false, needsGrading: false };
  }
  switch (q.type) {
    case "mcq": {
      const ok = String(answer) === String(q.correct_answer);
      return { score: ok ? q.marks : 0, isCorrect: ok, needsGrading: false };
    }
    case "true_false": {
      const ok = String(answer).toLowerCase() === String(q.correct_answer ?? "").toLowerCase();
      return { score: ok ? q.marks : 0, isCorrect: ok, needsGrading: false };
    }
    case "fill_blank": {
      let answers: string[] = [];
      try { answers = JSON.parse(q.correct_answer ?? "[]"); } catch { answers = [String(q.correct_answer ?? "")]; }
      const given = Array.isArray(answer) ? answer : [String(answer)];
      const total = answers.length || 1;
      let right = 0;
      for (let i = 0; i < total; i++) {
        const accepted = String(answers[i] ?? "").split("|").map(norm);
        if (accepted.includes(norm(String(given[i] ?? "")))) right++;
      }
      const ratio = right / total;
      return { score: +(q.marks * ratio).toFixed(2), isCorrect: ratio === 1, needsGrading: false };
    }
    case "matching": {
      let map: Record<string, string> = {};
      try { map = JSON.parse(q.correct_answer ?? "{}"); } catch { map = {}; }
      const keys = Object.keys(map);
      const total = keys.length || 1;
      const given = (typeof answer === "object" && !Array.isArray(answer)) ? answer as Record<string, string> : {};
      let right = 0;
      for (const k of keys) if (String(given[k] ?? "") === String(map[k])) right++;
      const ratio = right / total;
      return { score: +(q.marks * ratio).toFixed(2), isCorrect: ratio === 1, needsGrading: false };
    }
    case "short": {
      const accepted = String(q.correct_answer ?? "").split("|").map(norm);
      const ok = accepted.includes(norm(String(answer)));
      return { score: ok ? q.marks : 0, isCorrect: ok, needsGrading: false };
    }
    case "essay":
      return { score: 0, isCorrect: null, needsGrading: true };
  }
}

export function shuffle<T>(arr: T[], seed?: number): T[] {
  const out = [...arr];
  let s = seed ?? Math.floor(Math.random() * 1e9);
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export const QUESTION_TYPE_LABEL: Record<QType, string> = {
  mcq: "Multiple choice",
  true_false: "True / False",
  fill_blank: "Fill in the blanks",
  matching: "Matching",
  essay: "Essay",
  short: "Short answer",
};
